import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Shield, ShieldCheck, LogOut, Check, X, EyeOff, RotateCcw, Flag, ExternalLink, Loader2, UserCog } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { StarsDisplay } from "@/components/coachly/StarRating";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "coachly_admin_token";

function useAdminToken() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const save = (t) => {
    if (t) sessionStorage.setItem(STORAGE_KEY, t);
    else sessionStorage.removeItem(STORAGE_KEY);
    setToken(t);
  };
  return [token, save];
}

async function adminFetch(token, method, url, body) {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  if (method === "get") return api.get(url, config).then((r) => r.data);
  return api.post(url, body || {}, config).then((r) => r.data);
}

export default function Admin() {
  const [token, setToken] = useAdminToken();
  const [tab, setTab] = useState("applications");

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }
  return (
    <div data-testid={ADMIN.page} className="bg-slate-50 min-h-screen">
      <AdminHeader onLogout={() => setToken("")} />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-center gap-2 border-b border-slate-200 mb-8">
          <TabButton active={tab === "applications"} onClick={() => setTab("applications")} testId={ADMIN.tabApps}>
            Applications
          </TabButton>
          <TabButton active={tab === "reviews"} onClick={() => setTab("reviews")} testId={ADMIN.tabReviews}>
            Flagged reviews
          </TabButton>
          <TabButton active={tab === "queue"} onClick={() => setTab("queue")} testId="admin-tab-queue">
            Report queue
          </TabButton>
          <TabButton active={tab === "coaches"} onClick={() => setTab("coaches")} testId="admin-tab-coaches">
            Coaches
          </TabButton>
        </div>
        {tab === "applications" && <ApplicationsTab token={token} />}
        {tab === "reviews" && <ReviewsTab token={token} />}
        {tab === "queue" && <ReportQueueTab token={token} />}
        {tab === "coaches" && <CoachesTab token={token} />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative h-11 px-5 -mb-px text-[13.5px] font-medium transition-colors ${
        active ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
      {active && (
        <motion.div
          layoutId="admin-tab-underline"
          className="absolute inset-x-2 -bottom-px h-[2px] bg-slate-900"
        />
      )}
    </button>
  );
}

function AdminHeader({ onLogout }) {
  return (
    <header className="sticky top-0 inset-x-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-900">
          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a]">
            <span className="text-white font-display font-bold text-sm">C</span>
          </span>
          <span className="font-display font-semibold text-[17px] tracking-tighter">coachly<span className="text-[#10B981]">.</span></span>
          <span className="ml-3 hidden md:inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.2em] uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-700">
            <Shield className="w-3 h-3" strokeWidth={2.6} /> Admin
          </span>
        </Link>
        <button
          onClick={onLogout}
          data-testid={ADMIN.logout}
          className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </header>
  );
}

function AdminLogin({ onLogin }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // sanity-check by hitting a light admin route
      await api.get("/admin/applications", { headers: { Authorization: `Bearer ${token}` } });
      onLogin(token);
      toast.success("Signed in as admin.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid token.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={ADMIN.page} className="min-h-screen bg-slate-900 grid place-items-center px-6">
      <motion.form
        onSubmit={submit}
        data-testid={ADMIN.loginForm}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white rounded-3xl p-8"
      >
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">
          <Shield className="w-3.5 h-3.5" strokeWidth={2.6} /> Admin
        </div>
        <h1 className="mt-4 font-display font-medium text-3xl md:text-4xl tracking-tight text-slate-900">
          Sign in to moderate.
        </h1>
        <p className="mt-2 text-slate-500 text-[14px]">Paste the admin token from your <code>backend/.env</code>.</p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          data-testid={ADMIN.tokenInput}
          placeholder="admin-token"
          className="mt-6 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 font-mono"
          required
        />
        <button
          type="submit"
          disabled={busy || !token.trim()}
          data-testid={ADMIN.loginSubmit}
          className="mt-4 w-full h-12 rounded-xl bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60"
        >
          {busy ? "Verifying…" : "Enter admin"}
        </button>
        {error && <div className="mt-3 text-[13px] text-red-500">{error}</div>}
        <Link to="/" className="mt-6 block text-center text-[12.5px] text-slate-400 hover:text-slate-900 transition-colors">
          ← Back to Coachly
        </Link>
      </motion.form>
    </div>
  );
}

function ApplicationsTab({ token }) {
  const [status, setStatus] = useState("pending");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(token, "get", `/admin/applications?status=${status}`);
      setApps(res.applications || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "approve") {
        const res = await adminFetch(token, "post", `/admin/applications/${id}/approve`);
        if (res.onboarding_email_sent) {
          toast.success(`Approved · Stripe onboarding link sent to ${res.coach_slug}`, { duration: 5000 });
        } else {
          toast.success(`Approved · coach live at /coach/${res.coach_slug} (Stripe not provisioned — retry via 'Resend link')`);
        }
      } else if (action === "under_review") {
        await adminFetch(token, "post", `/admin/applications/${id}/mark-under-review`);
        toast.success("Marked under review");
      } else {
        await adminFetch(token, "post", `/admin/applications/${id}/reject`, { reason: "Does not meet current criteria" });
        toast.success("Application rejected");
      }
      setApps((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <FilterBar
        options={[
          { key: "pending", label: "Pending" },
          { key: "under_review", label: "Under review" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "all", label: "All" },
        ]}
        value={status}
        onChange={setStatus}
      />

      {loading ? (
        <LoadingRow />
      ) : apps.length === 0 ? (
        <EmptyState title="No applications" text={`Nothing ${status === "all" ? "on record" : `in "${status}"`} right now.`} />
      ) : (
        <ul data-testid={ADMIN.appsList} className="space-y-4">
          {apps.map((app) => (
            <motion.li
              key={app.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              data-testid={ADMIN.appItem(app.id)}
              className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6"
            >
              <div className="flex items-start gap-4">
                {app.avatar_url ? (
                  <img src={app.avatar_url} alt="" className="w-14 h-14 rounded-xl object-cover ring-1 ring-slate-200" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-slate-100 grid place-items-center text-slate-500 font-semibold">
                    {app.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-display font-semibold text-lg tracking-tight text-slate-900">{app.name}</h3>
                    <span className="text-[13px] text-slate-500">· {app.city}</span>
                    <span className="text-[13px] text-slate-500">· ${app.hourly_rate}/hr</span>
                    <span className="text-[13px] text-slate-500">· {app.years_experience}y exp</span>
                  </div>
                  <p className="mt-1 text-[14px] text-slate-700">{app.headline}</p>
                  <p className="mt-2 text-[13px] text-slate-500 leading-relaxed line-clamp-3">{app.bio}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                    {app.specialties.slice(0, 6).map((s) => (
                      <span key={s} className="text-[10.5px] tracking-wide uppercase font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{s}</span>
                    ))}
                    <span className="text-[12px] text-slate-400 ml-2">{app.contact_email}</span>
                    {app.sample_link && (
                      <a href={app.sample_link} target="_blank" rel="noreferrer" className="text-[12px] text-[#2563EB] inline-flex items-center gap-1 ml-2 hover:underline">
                        Sample <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                {app.status === "pending" || app.status === "under_review" ? (
                  <div className="flex flex-col md:flex-row gap-2 shrink-0">
                    <button
                      onClick={() => act(app.id, "reject")}
                      disabled={busyId === app.id}
                      data-testid={ADMIN.appReject(app.id)}
                      className="h-10 px-4 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-700 hover:border-red-300 hover:text-red-600 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      <X className="w-4 h-4" strokeWidth={2} /> Reject
                    </button>
                    {app.status === "pending" && (
                      <button
                        onClick={() => act(app.id, "under_review")}
                        disabled={busyId === app.id}
                        data-testid={`admin-app-under-review-${app.id}`}
                        className="h-10 px-4 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-700 hover:border-amber-300 hover:text-amber-700 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        <Shield className="w-4 h-4" strokeWidth={2} /> Under review
                      </button>
                    )}
                    <button
                      onClick={() => act(app.id, "approve")}
                      disabled={busyId === app.id}
                      data-testid={ADMIN.appApprove(app.id)}
                      className="h-10 px-4 rounded-full text-[13px] font-semibold bg-slate-900 text-white hover:bg-[#10B981] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {busyId === app.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.4} />}
                      Approve
                    </button>
                  </div>
                ) : (
                  <StatusPill status={app.status} />
                )}
              </div>
              {app.status === "approved" && app.coach_slug && (
                <div className="mt-4 text-[12.5px]">
                  <Link to={`/coach/${app.coach_slug}`} className="text-[#2563EB] hover:underline inline-flex items-center gap-1">
                    View live profile <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </motion.li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewsTab({ token }) {
  const [status, setStatus] = useState("flagged");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(token, "get", `/admin/reviews?status=${status}`);
      setRows(res.reviews || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await adminFetch(token, "post", `/admin/reviews/${id}/${action}`);
      toast.success(action === "hide" ? "Review hidden" : "Review restored");
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <FilterBar
        options={[
          { key: "flagged", label: "Flagged" },
          { key: "hidden", label: "Hidden" },
          { key: "all", label: "All" },
        ]}
        value={status}
        onChange={setStatus}
        testIdPrefix="admin-reviews-filter"
      />

      {loading ? (
        <LoadingRow />
      ) : rows.length === 0 ? (
        <EmptyState title="Inbox zero" text={`No ${status === "all" ? "" : status + " "}reviews to review.`} />
      ) : (
        <ul data-testid={ADMIN.reviewsList} className="space-y-4">
          <AnimatePresence>
            {rows.map((r) => (
              <motion.li
                key={r.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                data-testid={ADMIN.reviewItem(r.id)}
                className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[14px] text-slate-900">{r.client_name}</span>
                      <span className="text-[12px] text-slate-400">{r.client_email}</span>
                      {r.coach_slug && (
                        <Link to={`/coach/${r.coach_slug}`} className="text-[12px] text-[#2563EB] hover:underline inline-flex items-center gap-1">
                          /{r.coach_slug} <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                      {r.report_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                          <Flag className="w-3 h-3" /> {r.report_count} report{r.report_count > 1 ? "s" : ""}
                        </span>
                      )}
                      {r.is_hidden && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </div>
                    <div className="mt-2"><StarsDisplay value={r.rating} size={13} /></div>
                    {r.comment && (
                      <p className="mt-3 text-[14px] leading-relaxed text-slate-700 whitespace-pre-wrap">{r.comment}</p>
                    )}
                    {r.reports && r.reports.length > 0 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-[12px] text-slate-500 hover:text-slate-900">Report reasons ({r.reports.length})</summary>
                        <ul className="mt-2 space-y-1 text-[12.5px] text-slate-500">
                          {r.reports.map((rep, i) => (
                            <li key={i} className="flex items-baseline gap-2">
                              <span className="text-slate-400">·</span>
                              <span className="italic">"{rep.reason || "(no reason)"}"</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-col md:flex-row gap-2 shrink-0">
                    {!r.is_hidden ? (
                      <button
                        onClick={() => act(r.id, "hide")}
                        disabled={busyId === r.id}
                        data-testid={ADMIN.reviewHide(r.id)}
                        className="h-10 px-4 rounded-full text-[13px] font-semibold bg-slate-900 text-white hover:bg-red-600 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" strokeWidth={2} />}
                        Hide
                      </button>
                    ) : (
                      <button
                        onClick={() => act(r.id, "restore")}
                        disabled={busyId === r.id}
                        data-testid={ADMIN.reviewRestore(r.id)}
                        className="h-10 px-4 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-700 hover:border-[#10B981] hover:text-[#10B981] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        <RotateCcw className="w-4 h-4" strokeWidth={2} /> Restore
                      </button>
                    )}
                  </div>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}

function FilterBar({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${o.key}` : undefined}
          className={`h-9 px-4 rounded-full text-[12.5px] font-medium border transition-colors ${
            value === o.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    approved: { bg: "bg-[#10B981]/10 text-[#059669] border-[#10B981]/30", label: "Approved" },
    rejected: { bg: "bg-red-50 text-red-600 border-red-200", label: "Rejected" },
    pending: { bg: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
    under_review: { bg: "bg-blue-50 text-[#2563EB] border-blue-200", label: "Under review" },
  }[status] || { bg: "bg-slate-100 text-slate-600 border-slate-200", label: status };
  return <span className={`inline-flex items-center h-8 px-3 rounded-full text-[11.5px] font-semibold border ${cfg.bg}`}>{cfg.label}</span>;
}

function LoadingRow() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="animate-pulse bg-white border border-slate-200 rounded-2xl h-24" />
      ))}
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div data-testid={ADMIN.emptyState} className="border border-dashed border-slate-200 rounded-2xl p-12 text-center bg-white">
      <div className="eyebrow">§ Empty</div>
      <h3 className="mt-3 font-display text-2xl tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-500">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Report Queue tab — one unified feed of open coach reports + flagged reviews
// with one-click "Resolve", "Hide coach", and "Hide review" actions.
// ---------------------------------------------------------------------------
function ReportQueueTab({ token }) {
  const [data, setData] = useState({ coach_reports: [], review_reports: [], counts: { total: 0 } });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(token, "get", "/admin/report-queue");
      setData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resolveCoachReport = async (id) => {
    setBusyId(id);
    try {
      await adminFetch(token, "post", `/admin/coach-reports/${id}/resolve`);
      toast.success("Report resolved");
      setData((d) => ({ ...d, coach_reports: d.coach_reports.filter((r) => r.id !== id) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resolve");
    } finally { setBusyId(null); }
  };

  const hideCoach = async (slug, id) => {
    setBusyId(id);
    try {
      await adminFetch(token, "post", `/admin/coaches/${slug}/hide`);
      toast.success(`${slug} hidden from marketplace`);
      // All open reports for that coach were auto-resolved server-side.
      setData((d) => ({ ...d, coach_reports: d.coach_reports.filter((r) => r.coach_slug !== slug) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not hide coach");
    } finally { setBusyId(null); }
  };

  const hideReview = async (id) => {
    setBusyId(id);
    try {
      await adminFetch(token, "post", `/admin/reviews/${id}/hide`);
      toast.success("Review hidden");
      setData((d) => ({ ...d, review_reports: d.review_reports.filter((r) => r.id !== id) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not hide review");
    } finally { setBusyId(null); }
  };

  if (loading) return <div className="text-slate-500 text-sm">Loading queue…</div>;
  const isEmpty = !data.coach_reports.length && !data.review_reports.length;
  if (isEmpty) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="Inbox zero."
        text="No open coach reports or flagged reviews. Come back after new activity."
      />
    );
  }

  return (
    <div data-testid="admin-report-queue" className="space-y-10">
      <div className="text-[13px] text-slate-500">
        {data.counts.total} open item{data.counts.total === 1 ? "" : "s"} · Newest first.
      </div>

      {data.coach_reports.length > 0 && (
        <section>
          <div className="eyebrow mb-4">§ Coach reports ({data.coach_reports.length})</div>
          <ul className="space-y-3" data-testid="admin-queue-coach-reports">
            {data.coach_reports.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-queue-coach-report-${r.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center h-6 px-2 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-semibold uppercase tracking-[0.18em]">
                      {r.reason || "report"}
                    </span>
                    <a href={`/coach/${r.coach_slug}`} target="_blank" rel="noreferrer" className="text-[13.5px] font-semibold text-slate-900 hover:text-[#2563EB]">
                      /coach/{r.coach_slug} ↗
                    </a>
                  </div>
                  {r.details && (
                    <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{r.details}</p>
                  )}
                  <p className="mt-2 text-[11.5px] text-slate-400">
                    Reported by {r.reporter_email || "anonymous"} · {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => resolveCoachReport(r.id)}
                    disabled={busyId === r.id}
                    data-testid={`admin-queue-resolve-${r.id}`}
                    className="h-9 px-3 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[12.5px] font-semibold disabled:opacity-50 transition-colors"
                  >
                    Resolve
                  </button>
                  <button
                    onClick={() => hideCoach(r.coach_slug, r.id)}
                    disabled={busyId === r.id}
                    data-testid={`admin-queue-hide-coach-${r.coach_slug}`}
                    className="h-9 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-[12.5px] font-semibold disabled:opacity-50 transition-colors"
                  >
                    Hide coach
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.review_reports.length > 0 && (
        <section>
          <div className="eyebrow mb-4">§ Flagged reviews ({data.review_reports.length})</div>
          <ul className="space-y-3" data-testid="admin-queue-review-reports">
            {data.review_reports.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-queue-review-${r.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center h-6 px-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold uppercase tracking-[0.18em]">
                      {r.rating}★ · flagged
                    </span>
                    <a href={`/coach/${r.coach_slug}`} target="_blank" rel="noreferrer" className="text-[13.5px] font-semibold text-slate-900 hover:text-[#2563EB]">
                      /coach/{r.coach_slug} ↗
                    </a>
                  </div>
                  {r.comment && (
                    <p className="mt-2 text-[13px] text-slate-700 leading-relaxed">"{r.comment}"</p>
                  )}
                  <p className="mt-2 text-[11.5px] text-slate-400">
                    From {r.client_name || r.client_email || "anonymous"} · {r.report_count || 1} flag(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => hideReview(r.id)}
                    disabled={busyId === r.id}
                    data-testid={`admin-queue-hide-review-${r.id}`}
                    className="h-9 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-[12.5px] font-semibold disabled:opacity-50 transition-colors"
                  >
                    Hide review
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CoachesTab — approved coach directory with "Sign in as this coach" impersonation.
// Admin-gated; each impersonation is audit-logged server-side.
// -----------------------------------------------------------------------------
function CoachesTab({ token }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const { refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      // Public marketplace endpoint returns approved coaches with all UI fields.
      const r = await api.get("/coaches", { params: { limit: 200 } });
      setCoaches(r.data.coaches || []);
    } catch (e) {
      toast.error("Couldn't load coaches.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const impersonate = async (coach) => {
    if (!window.confirm(`Sign in as ${coach.name}? You'll be redirected to their coach dashboard. This action is audit-logged.`)) {
      return;
    }
    setBusySlug(coach.slug);
    try {
      // Backend sets the httpOnly session cookie on this SAME response,
      // so we just need to re-read /auth/me and navigate.
      await api.post(`/admin/coaches/${coach.slug}/impersonate`, null, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      await refreshAuth();
      toast.success(`Signed in as ${coach.name}. Session expires in 2h.`);
      navigate("/coach-dashboard");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impersonation failed.";
      toast.error(msg);
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div data-testid="admin-coaches-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-slate-900">Approved coaches</h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Impersonate any coach to walk through their dashboard — payouts, availability, inbox, reviews.
          </p>
        </div>
        <button
          onClick={load}
          className="h-9 px-3 rounded-full border border-slate-200 hover:border-slate-400 text-[12.5px] font-medium text-slate-700 inline-flex items-center gap-1.5"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-14 text-center text-[13px] text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
          Loading coaches…
        </div>
      ) : coaches.length === 0 ? (
        <div className="py-14 text-center text-[13px] text-slate-500">No approved coaches yet.</div>
      ) : (
        <ul className="grid gap-3" data-testid="admin-coaches-list">
          {coaches.map((c) => (
            <li
              key={c.slug}
              data-testid={`admin-coach-row-${c.slug}`}
              className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
            >
              {c.photo_url ? (
                <img src={c.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center text-slate-500 font-semibold">
                  {(c.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  {c.payments_ready && (
                    <span className="text-[10.5px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Stripe live
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-slate-500 truncate">
                  {c.headline || c.specialty}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  slug: <span className="font-mono">{c.slug}</span>
                </div>
              </div>
              <Link
                to={`/coach/${c.slug}`}
                target="_blank"
                className="text-slate-400 hover:text-slate-900 p-2"
                title="Open public profile"
              >
                <ExternalLink className="w-4 h-4" />
              </Link>
              <button
                onClick={() => impersonate(c)}
                disabled={busySlug === c.slug}
                data-testid={`admin-impersonate-${c.slug}`}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[12.5px] font-semibold transition-colors disabled:opacity-60"
              >
                {busySlug === c.slug ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <UserCog className="w-3.5 h-3.5" strokeWidth={2.2} />
                )}
                Sign in as coach
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

